import { createRouter } from "@tanstack/react-router";
import * as z from "zod";

import apiClient from "@/api/apiClient";
import queryClient from "@/api/queryClient";
import RouterError from "@/components/Router/RouterError";
import RouterLoading from "@/components/Router/RouterLoading";
import RouterNotFound from "@/components/Router/RouterNotFound";
import { dataTableSearchSchema, DEFAULT_DATATABLE_OPTIONS } from "@/configs/datatable";
import { defaultGlobalFilters, globalFiltersSearchSchema } from "@/providers/globalFiltersConfig";
import { routeTree, type FileRoutesByTo } from "@/routeTree.gen";

import type { QueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export interface BreadcrumbsContext {
  label: string;
  icon?: ReactNode;
  path?: keyof FileRoutesByTo;
}

export interface RouterContext {
  apiClient: typeof apiClient;
  queryClient: QueryClient;
  breadcrumbs: BreadcrumbsContext[];
}

export const router = createRouter({
  scrollRestoration: true,
  routeTree,
  context: {
    apiClient,
    queryClient,
    breadcrumbs: [] as BreadcrumbsContext[],
  },
  defaultPendingMs: 250,
  defaultPendingMinMs: 250,
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  // ! TEMPORARILY DISABLED, since preload was not triggering login-redirects properly on missing auth claims
  // defaultPreload: "intent",
  // defaultPreloadStaleTime: 0,
  // defaultPreloadDelay: 100,
  defaultErrorComponent: RouterError,
  defaultPendingComponent: RouterLoading,
  defaultNotFoundComponent: RouterNotFound,
  defaultStructuralSharing: true,
});

// router search params typed, used in _auth.tsx router file
export const routerSearchParamsSchema = z.object({
  ...globalFiltersSearchSchema.shape,
  ...dataTableSearchSchema.shape,
});

// default values that are stripped from search params
export const routerSearchParamsStrip = {
  ...defaultGlobalFilters,
  ...DEFAULT_DATATABLE_OPTIONS,
};
